import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Archive,
  ArrowLeft,
  Bot,
  FileText,
  Save,
} from "lucide-react";
import {
  knowledgeGetKnowledgeItem,
  knowledgeGetKnowledgeItemChunks,
  knowledgeUpdateKnowledgeItem,
  knowledgeRollbackKnowledgeItem,
} from "../gen";
import type { KnowledgeItemResponse } from "../gen/models";
import { useToast } from "../context/ToastContext";
import { useAgentOptions } from "../hooks/useAgentOptions";
import { ChunkInspectorPanel } from "../components/knowledge/ChunkInspectorPanel";
import { UnifiedRevisionModal } from "../components/common/UnifiedRevisionModal";
import { TimeAgo, getKnowledgeItemCreator } from "../components/common/TimeAgo";
import { normalizeAgentType, sanitizeProtocoloName } from "../hooks/useUserDirectory";

export function KnowledgeDetail() {
  const { item_id } = useParams<{ item_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const agentOptions = useAgentOptions({ includeUniversal: true });

  const [item, setItem] = useState<KnowledgeItemResponse | null>(null);
  const [chunks, setChunks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit / Revision Modal state
  const [isRevising, setIsRevising] = useState(false);
  const [revisionTitle, setRevisionTitle] = useState("");
  const [revisionCategory, setRevisionCategory] = useState("logistics");
  const [revisionAgentType, setRevisionAgentType] = useState("universal");
  const [revisionContent, setRevisionContent] = useState("");

  const getAgentLabel = (agentType?: string | null) => {
    if (!agentType || agentType === "universal") {
      return "Universal (All Agents)";
    }
    const norm = normalizeAgentType(agentType);
    const match = agentOptions.find((a) => a.value === norm || a.value === agentType);
    return match?.label || sanitizeProtocoloName(norm, "Sofia");
  };

  const loadItemAndChunks = async () => {
    if (!item_id) return;
    setLoading(true);
    setError(null);
    try {
      const [itRes, chkRes] = await Promise.all([
        knowledgeGetKnowledgeItem(item_id),
        knowledgeGetKnowledgeItemChunks(item_id),
      ]);
      const current = (itRes as any).data as KnowledgeItemResponse;
      setItem(current);
      setChunks(((chkRes as any).data || []) as any[]);
    } catch (err: any) {
      setError(err?.message || "Failed to load knowledge document");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItemAndChunks();
  }, [item_id]);

  const handleOpenRevisionModal = () => {
    if (!item) return;
    setRevisionTitle(item.title);
    setRevisionCategory(item.category);
    setRevisionAgentType(item.agent_type || "universal");
    setRevisionContent(item.content);
    setIsRevising(true);
  };

  const handleConfirmRevision = async () => {
    if (!item_id || !revisionTitle.trim() || !revisionContent.trim()) {
      toast.warning("Title and content cannot be empty.");
      return;
    }
    setSaving(true);
    try {
      await knowledgeUpdateKnowledgeItem(item_id, {
        title: revisionTitle.trim(),
        category: revisionCategory,
        agent_type: revisionAgentType === "universal" ? "universal" : revisionAgentType,
        content: revisionContent.trim(),
      });
      setIsRevising(false);
      await loadItemAndChunks();
      toast.success("Document updated, re-indexed, and new version created.");
    } catch (err: any) {
      toast.error("Failed to update: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRollback = async () => {
    if (!item_id) return;
    const reason = prompt("Reason for archiving this document?");
    if (reason === null) return;

    setSaving(true);
    try {
      await knowledgeRollbackKnowledgeItem(item_id, { reason });
      loadItemAndChunks();
      toast.success("Document archived successfully.");
    } catch (err: any) {
      toast.error("Failed to archive: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-12 text-center text-stone-400 dark:text-stone-500 text-xs">
        Loading document chunks & embedding vector metadata...
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="p-8 text-center bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 text-rose-600 dark:text-rose-400 text-xs">
          {error || "Knowledge item not found"}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Top Header */}
      <div className="space-y-4">
        <div className="md:hidden pb-1">
          <button
            type="button"
            onClick={() => navigate(`/knowledge${location.search}`)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Back to Knowledge Base</span>
          </button>
        </div>
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-2xs flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="px-2.5 py-0.5 rounded-md text-xs font-bold uppercase tracking-wider bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900">
                {item.category}
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-semibold bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border border-stone-200 dark:border-stone-700">
                <Bot className="w-3.5 h-3.5 text-stone-500 dark:text-stone-400" />
                <span>{getAgentLabel(item.agent_type)}</span>
              </span>
              <h1 className="text-xl font-bold tracking-tight text-stone-900 dark:text-stone-100">
                {item.title}
              </h1>
              <span
                className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize border ${
                  item.status === "active"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800/80"
                    : "bg-stone-100 text-stone-600 border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700"
                }`}
              >
                {item.status}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs text-stone-400 dark:text-stone-500 pt-1">
              <span>ID: <code className="font-mono text-stone-600 dark:text-stone-400">{item.id}</code></span>
              <span>•</span>
              {getKnowledgeItemCreator(item) && (
                <>
                  <span>
                    Created by: <strong className="text-stone-700 dark:text-stone-300 capitalize">{getKnowledgeItemCreator(item)}</strong>
                  </span>
                  <span>•</span>
                </>
              )}
              <span>
                Created: <TimeAgo date={item.created_at} className="text-stone-700 dark:text-stone-300 font-medium" />
              </span>
              <span>•</span>
              <span>
                Updated: <TimeAgo date={item.updated_at} titlePrefix="Updated: " className="text-stone-700 dark:text-stone-300 font-medium" />
              </span>
              <span>•</span>
              <span>Source: <strong className="text-stone-700 dark:text-stone-300 capitalize">{item.source_type}</strong></span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleOpenRevisionModal}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition shadow-2xs disabled:opacity-50 cursor-pointer"
            >
              <Save className="w-3.5 h-3.5" />
              <span>New Version</span>
            </button>

            {item.status === "active" && (
              <button
                type="button"
                onClick={handleRollback}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-xs font-medium text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-50 dark:hover:bg-stone-800 transition shadow-2xs disabled:opacity-50 cursor-pointer"
              >
                <Archive className="w-3.5 h-3.5" />
                <span>Archive Document</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 2-Column Layout: Document Viewer (Left) + Chunk Segment Inspector (Right) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Left Column: Read-Only Document Viewer */}
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-2xs space-y-4">
          <div className="border-b border-stone-100 dark:border-stone-800 pb-3 flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-wider text-stone-800 dark:text-stone-200 flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <span>Document Content</span>
            </h3>
            <span className="text-[11px] text-stone-400 dark:text-stone-500 font-mono">
              {item.content.length} characters
            </span>
          </div>

          <div className="space-y-4">
            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400 dark:text-stone-500 block mb-1">
                Title
              </span>
              <div className="text-sm font-semibold text-stone-900 dark:text-stone-100">
                {item.title}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400 dark:text-stone-500 block mb-1">
                  Target Agent
                </span>
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border border-stone-200 dark:border-stone-700">
                  <Bot className="w-3.5 h-3.5 text-stone-500 dark:text-stone-400" />
                  <span>{getAgentLabel(item.agent_type)}</span>
                </span>
              </div>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400 dark:text-stone-500 block mb-1">
                  Category
                </span>
                <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 capitalize">
                  {item.category}
                </span>
              </div>
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400 dark:text-stone-500 block mb-1">
                  Status
                </span>
                <span className="text-xs font-medium text-stone-600 dark:text-stone-400 capitalize">
                  {item.status}
                </span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400 dark:text-stone-500 block">
                  Body Text
                </span>
                <span className="text-[11px] text-stone-400 dark:text-stone-500">
                  Read-only (use New Version to edit)
                </span>
              </div>
              <div className="rounded-lg border border-stone-200 dark:border-stone-800 bg-stone-50/70 dark:bg-stone-950/70 p-4 font-mono text-xs text-stone-800 dark:text-stone-200 leading-relaxed whitespace-pre-wrap max-h-96 overflow-y-auto select-text">
                {item.content}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Chunk Inspector & Vector Embeddings */}
        <ChunkInspectorPanel chunks={chunks} />
      </div>

      {/* Unified Revision Modal Dialog */}
      <UnifiedRevisionModal
        isOpen={isRevising}
        onClose={() => setIsRevising(false)}
        title="Edit Knowledge Document (New Version)"
        subtitle="Knowledge documents cannot be edited in place. Edit your text below, inspect the live diff, and confirm to save and re-index vector embeddings."
        fields={[
          {
            label: "Document Title",
            type: "text",
            value: revisionTitle,
            onChange: setRevisionTitle,
            placeholder: "Document title...",
          },
          {
            label: "Target Agent",
            type: "select",
            value: revisionAgentType,
            onChange: setRevisionAgentType,
            options: agentOptions.map((opt) => ({
              value: opt.value,
              label: opt.label === "Universal" ? "Universal (All Agents)" : opt.label,
            })),
          },
          {
            label: "Category",
            type: "select",
            value: revisionCategory,
            onChange: setRevisionCategory,
            options: [
              ...(item?.category ? [{ value: item.category, label: item.category }] : []),
              { value: "logistics", label: "Logistics & Transfers" },
              { value: "villas", label: "Villas & Residences" },
              { value: "policies", label: "Policies & Cancellation" },
              { value: "culinary", label: "Culinary & Dining" },
              { value: "activities", label: "Activities & Surf" },
              { value: "operations", label: "General Operations" },
              { value: "feedback", label: "Feedback & Curation" },
            ].filter((v, i, a) => a.findIndex((t) => t.value === v.value) === i),
          },
        ]}
        contentLabel="Document Content"
        contentValue={revisionContent}
        onChangeContent={setRevisionContent}
        contentRows={8}
        oldTitle={`${item.title} (Current)`}
        newTitle={`${revisionTitle || item.title} (New Version)`}
        oldContent={item.content}
        newContent={revisionContent}
        confirmLabel="Save New Version & Re-Index"
        onConfirm={handleConfirmRevision}
        submitting={saving}
      />
    </div>
  );
}
